import { useCallback, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  createCoachSeries,
  listTables,
  listUsers,
  slotConflictDetail,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import { Field } from '../../components/Field';
import { SeriesConflictsBanner } from '../../components/SeriesConflictsBanner';
import { SlotWindowFields } from '../../components/SlotWindowFields';
import { useToast } from '../../hooks/useToast';
import { todayLocal } from '../../lib/time';
import { slotWindowError } from '../../lib/coach-session-window';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';
import type {
  AdminLookupUser,
  Paged,
  SeriesConflictsDetail,
  TableEmbed,
} from '../../lib/types';
import { WEEKDAY_LABELS } from '../../lib/coach-series';

const WEEKDAY_OPTIONS = WEEKDAY_LABELS.map((label, value) => ({ value, label }));

export function NewCoachSeriesPage() {
  const navigate = useNavigate();
  const { push } = useToast();

  // Tables for the dropdown.
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  // All coaches for the dropdown. Fetch up to 200 users and filter — coaches
  // are a small list (typically < 20), so client-side filter is fine.
  const coachesLoader = useCallback(
    (signal: AbortSignal) => listUsers({ limit: 200 }, signal),
    [],
  );
  const usersPage = useApi<Paged<AdminLookupUser>>(coachesLoader);
  const coaches = (usersPage.data?.items ?? []).filter((u) => u.is_coach);

  // Selected coach id (the AdminLookupUser is resolved from `coaches` on use).
  const [coachId, setCoachId] = useState<string>('');
  const coach = coaches.find((c) => c.id === coachId) ?? null;

  // Form fields.
  const [tableId, setTableId] = useState<string>('');
  const [dayOfWeek, setDayOfWeek] = useState<number>(0);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startsOn, setStartsOn] = useState(todayLocal());
  const [endsOn, setEndsOn] = useState('');

  // Submission state.
  const [submitting, setSubmitting] = useState(false);
  // The whole 409 body, not just the array: `next_free_time` lives at the top
  // level of it (givip/prospin-api#121) and the banner renders it.
  const [conflicts, setConflicts] = useState<SeriesConflictsDetail | null>(null);

  // Shared with the one-time form (admin#47) so both create paths agree on what
  // a legal window is. The minimum-duration threshold stays the API's — see
  // lib/coach-session-window.ts.
  const windowError = slotWindowError(startTime, endTime);

  const canSubmit =
    coach !== null &&
    tableId !== '' &&
    startTime !== '' &&
    endTime !== '' &&
    startsOn !== '' &&
    windowError === null &&
    !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !coach) return;
    setSubmitting(true);
    setConflicts(null);
    try {
      const res = await createCoachSeries({
        coach_user_id: coach.id,
        table_id: Number(tableId),
        day_of_week: dayOfWeek,
        start_time: startTime,
        end_time: endTime,
        starts_on: startsOn,
        ends_on: endsOn || null,
      });
      push(
        'success',
        `Series #${res.series.id} created. ${res.occurrences_inserted} occurrence${res.occurrences_inserted === 1 ? '' : 's'} materialized.`,
      );
      navigate(`/coaches/series/${res.series.id}`);
    } catch (err) {
      // The shared narrower, same as the one-time form (admin#47) — the 409 is
      // the canonical conflict envelope since api#121, so nothing here needs to
      // cast `err.detail` by hand. `code` is what makes it *this* conflict.
      const d = slotConflictDetail(err);
      if (d?.code === 'series_conflicts') {
        setConflicts(d);
        push(
          'error',
          `${d.conflicts.length} conflict${d.conflicts.length === 1 ? '' : 's'} — resolve below and retry.`,
        );
        return;
      }
      push('error', err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>New coach series</h1>
        <p style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          Recurring weekly slot. The API materializes occurrences on a rolling 30-day horizon.
        </p>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          background: PS.white,
          padding: 20,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
        }}
      >
        <Field label="Coach">
          <select
            value={coachId}
            onChange={(e) => setCoachId(e.target.value)}
            style={selectStyle}
          >
            <option value="">
              {usersPage.loading
                ? 'Loading coaches…'
                : coaches.length === 0
                  ? 'No coaches — promote one in /coaches first'
                  : '— pick a coach —'}
            </option>
            {coaches.map((c) => (
              <option key={c.id} value={c.id}>
                {fullName(c)}{c.phone ? ` · ${c.phone}` : ''}
              </option>
            ))}
          </select>
        </Field>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Table">
            <select
              value={tableId}
              onChange={(e) => setTableId(e.target.value)}
              style={selectStyle}
            >
              <option value="">—</option>
              {(tables.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Weekday">
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              style={selectStyle}
            >
              {WEEKDAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <SlotWindowFields
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
        />

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Starts on">
            <DatePicker value={startsOn} onChange={setStartsOn} />
          </Field>
          <Field label="Ends on (optional)">
            <DatePicker value={endsOn} onChange={setEndsOn} />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create series'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/coaches')}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>

      {conflicts && (
        <SeriesConflictsBanner
          detail={conflicts}
          // Once any conflict is resolved, clear the banner. Re-submitting
          // the form will either succeed or return a fresh conflict list.
          onResolved={() => setConflicts(null)}
        />
      )}
    </div>
  );
}

const selectStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  background: PS.white,
};

